import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  changeQty,
  openOrderForTable,
  saveItems,
  type DiningTable,
  type Order,
  type Zone,
} from '../lib/db'
import { elapsedMinutes, elapsedText, money, qtyToText, whenText, QTY_UNIT } from '../lib/money'
import { useMediaQuery } from '../lib/hooks'
import { PageBack } from './PageBack'
import { NoteSheet } from './NoteSheet'
import { Thumb } from './Thumb'

/**
 * Màn hình bán tại bàn của bộ Modern.
 *
 * Hai khoang: sơ đồ bàn bên trái, đơn của bàn đang chọn bên phải. Máy quầy nằm ngang có
 * thừa chỗ cho cả hai, và khi có cả hai thì người bán trả lời được câu hỏi hay gặp nhất
 * — "bàn 3 đang ăn những gì, hết bao nhiêu rồi" — mà không phải rời khỏi sơ đồ.
 *
 * Màn hình hẹp không đủ chỗ cho khoang bên phải, nên ở đó chạm vào bàn là đi thẳng sang
 * màn hình gọi món như cũ. Một chạm, không thêm bước nào.
 */

/** Bàn ngồi quá lâu thì đổi sang cảnh báo — quán đông, cần biết bàn nào sắp nên nhắc. */
const LONG_SIT_MINUTES = 90

const ALL = 'all'

type TableState = 'free' | 'busy' | 'long' | 'reserved'

function stateOf(table: DiningTable, order: Order | undefined, now: number): TableState {
  if (order && order.status === 'Open') {
    return elapsedMinutes(order.openedAt, now) >= LONG_SIT_MINUTES ? 'long' : 'busy'
  }
  if (table.reservedAt && new Date(table.reservedAt).getTime() > now - 30 * 60_000) return 'reserved'
  return 'free'
}

const STATE_LABEL: Record<TableState, string> = {
  free: 'Trống',
  busy: 'Đang phục vụ',
  long: 'Ngồi lâu',
  reserved: 'Đã đặt trước',
}

interface TileProps {
  table: DiningTable
  order: Order | undefined
  now: number
  selected: boolean
  onClick: () => void
}

function TableTile({ table, order, now, selected, onClick }: TileProps) {
  const state = stateOf(table, order, now)
  const busy = state === 'busy' || state === 'long'

  return (
    <button
      type="button"
      className={`tile-table is-${state}${selected ? ' is-picked' : ''}`}
      onClick={onClick}
      aria-pressed={selected}
      aria-label={
        busy && order
          ? `${table.name}, đang có khách, ${money(order.total)} đồng, ngồi ${elapsedText(order.openedAt, now)}`
          : state === 'reserved'
            ? `${table.name}, đã đặt trước ${whenText(table.reservedAt!, new Date(now))}`
            : `${table.name}, trống`
      }
    >
      <span className="tile-badge">{STATE_LABEL[state]}</span>

      <span className="tile-disc" aria-hidden="true">
        {table.icon ?? '🪑'}
      </span>

      <b className="tile-table-name">{table.name}</b>
      <small className="tile-seats">{table.seats > 0 ? `${table.seats} người` : 'Mang về'}</small>

      {busy && order ? (
        <span className="tile-money num">
          {money(order.total)}
          <em>{elapsedText(order.openedAt, now)}</em>
        </span>
      ) : state === 'reserved' ? (
        <span className="tile-when">{whenText(table.reservedAt!, new Date(now))}</span>
      ) : null}
    </button>
  )
}

interface Props {
  zones: Zone[]
  tables: DiningTable[]
  ordersByTable: Map<string, Order>
  now: number
  owner: boolean
  onArrange: () => void
  onEditTable: (t: DiningTable) => void
  onReserve: (t: DiningTable) => void
}

export function TableBoard({
  zones,
  tables,
  ordersByTable,
  now,
  owner,
  onArrange,
  onEditTable,
  onReserve,
}: Props) {
  const nav = useNavigate()

  // Cùng một mốc với câu @media đang ẩn khoang bên phải. Hai chỗ phải khớp nhau, không
  // thì có màn hình chạm vào bàn mà chẳng thấy gì xảy ra.
  const hasPane = useMediaQuery('(min-width: 1180px)')

  const [zone, setZone] = useState<string>(ALL)
  const [query, setQuery] = useState('')
  const [pickedId, setPickedId] = useState<string | null>(null)
  const [noting, setNoting] = useState<Order['items'][number] | null>(null)

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    return tables.filter(
      (t) => (zone === ALL || t.zoneId === zone) && (!q || t.name.toLowerCase().includes(q)),
    )
  }, [tables, zone, query])

  // Bàn đang chọn có thể vừa bị lọc mất khỏi danh sách, hoặc vừa bị xoá ở máy khác.
  const picked = shown.find((t) => t.id === pickedId) ?? null
  const order = picked ? ordersByTable.get(picked.id) : undefined

  const busyCount = shown.filter((t) => ordersByTable.get(t.id)).length

  async function openOrder(table: DiningTable) {
    const o = await openOrderForTable(table)
    nav(`/order/${o.id}`)
  }

  async function bump(item: Order['items'][number], delta: number) {
    if (!order) return
    await saveItems(order.id, changeQty(order.items, item.id, delta * QTY_UNIT))
  }

  async function drop(itemId: string) {
    if (!order) return
    await saveItems(
      order.id,
      order.items.filter((i) => i.id !== itemId),
    )
  }

  return (
    <div className="board tables-board">
      <header className="page-head">
        <PageBack to={'/'} />
        <span className="page-icon" aria-hidden="true">
          🪑
        </span>
        <div className="page-title">
          <h1>Bán tại bàn</h1>
          <p>Chọn bàn để bắt đầu gọi món</p>
        </div>

        <label className="search">
          <span className="sr-only">Tìm bàn</span>
          <span className="search-glyph" aria-hidden="true">
            ⌕
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Tìm bàn…"
            inputMode="search"
          />
        </label>

        {owner && (
          <button
            type="button"
            className="icon-btn"
            onClick={onArrange}
            aria-label="Sắp xếp bàn"
            title="Sắp xếp bàn"
          >
            ✥
          </button>
        )}
      </header>

      <div className="tables-split">
        <div className="tables-main">
          <div className="zone-row">
            <div className="chip-row">
              <button
                type="button"
                className="chip"
                aria-pressed={zone === ALL}
                onClick={() => setZone(ALL)}
              >
                Tất cả
              </button>
              {zones.map((z) => (
                <button
                  key={z.id}
                  type="button"
                  className="chip"
                  aria-pressed={zone === z.id}
                  onClick={() => setZone(z.id)}
                >
                  {z.name}
                </button>
              ))}
            </div>

            <span className="zone-count">
              {busyCount > 0 ? `${busyCount}/${shown.length} bàn có khách` : `Tổng bàn: ${shown.length}`}
            </span>
          </div>

          {shown.length === 0 ? (
            <div className="empty">
              <div className="empty-icon" aria-hidden="true">
                🪑
              </div>
              <p>{query ? `Không có bàn nào tên "${query}".` : 'Khu vực này chưa có bàn nào.'}</p>
            </div>
          ) : (
            <div className="tile-tables">
              {shown.map((t) => (
                <TableTile
                  key={t.id}
                  table={t}
                  order={ordersByTable.get(t.id)}
                  now={now}
                  selected={hasPane && t.id === pickedId}
                  onClick={() => (hasPane ? setPickedId(t.id) : void openOrder(t))}
                />
              ))}
            </div>
          )}
        </div>

        <aside className="order-pane" aria-label="Đơn của bàn đang chọn">
          {!picked ? (
            <div className="pane-empty">
              <div className="empty-icon" aria-hidden="true">
                👆
              </div>
              <p>Chạm vào một bàn để xem đơn và thu tiền.</p>
            </div>
          ) : (
            <>
              <div className="pane-head">
                <span className="pane-name">{picked.name}</span>
                {/* Ô mang về đã mang chữ "Mang về" ngay trên cái tên rồi, khỏi nhắc lại. */}
                <span className="pane-seats">{picked.seats > 0 ? `${picked.seats} người` : ''}</span>
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => onEditTable(picked)}
                  aria-label={`Sửa ${picked.name}`}
                  title="Sửa bàn"
                >
                  ✎
                </button>
              </div>

              {order && order.items.length > 0 ? (
                <>
                  <div className="pane-lines">
                    {order.items.map((i) => (
                      <div key={i.id} className="pane-line">
                        <Thumb name={i.name} imageKey={null} className="pane-thumb" />

                        <div className="pane-line-main">
                          <button type="button" className="line-note" onClick={() => setNoting(i)}>
                            <b>
                              {i.name}
                              {i.optionLabel && <em> · {i.optionLabel}</em>}
                            </b>
                            {i.note ? (
                              <small className="line-note-text">{i.note}</small>
                            ) : (
                              <small className="line-note-add">+ ghi chú</small>
                            )}
                          </button>
                          <div className="stepper">
                            <button
                              type="button"
                              onClick={() => void bump(i, -1)}
                              aria-label={`Bớt ${i.name}`}
                            >
                              −
                            </button>
                            <span className="num">{qtyToText(i.qtyMilli)}</span>
                            <button
                              type="button"
                              onClick={() => void bump(i, 1)}
                              aria-label={`Thêm ${i.name}`}
                            >
                              +
                            </button>
                          </div>
                        </div>

                        <span className="pane-line-total num">{money(i.lineTotal)}</span>

                        <button
                          type="button"
                          className="pane-drop"
                          onClick={() => void drop(i.id)}
                          aria-label={`Bỏ ${i.name} khỏi đơn`}
                          title="Bỏ món"
                        >
                          🗑
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="pane-total">
                    <span>Tổng tiền</span>
                    <b className="num">{money(order.total)}đ</b>
                  </div>

                  <div className="pane-actions">
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => nav(`/order/${order.id}`)}
                    >
                      + Gọi thêm
                    </button>
                    <button
                      type="button"
                      className="btn btn-brand grow"
                      onClick={() => nav(`/pay/${order.id}`)}
                    >
                      Thanh toán <span aria-hidden="true">›</span>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="pane-empty">
                    <div className="empty-icon" aria-hidden="true">
                      {picked.icon ?? '🪑'}
                    </div>
                    <p>
                      {picked.reservedAt
                        ? `${picked.reservedName ?? 'Khách đặt'} · ${whenText(picked.reservedAt, new Date(now))}`
                        : 'Bàn đang trống.'}
                    </p>
                  </div>

                  <div className="pane-actions">
                    <button type="button" className="btn btn-ghost" onClick={() => onReserve(picked)}>
                      ⏰ Đặt trước
                    </button>
                    <button
                      type="button"
                      className="btn btn-brand grow"
                      onClick={() => void openOrder(picked)}
                    >
                      Gọi món <span aria-hidden="true">›</span>
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </aside>
      </div>

      {noting && order && (
        <NoteSheet
          item={noting}
          onClose={() => setNoting(null)}
          onSave={async (note) => {
            await saveItems(
              order.id,
              order.items.map((x) => (x.id === noting.id ? { ...x, note } : x)),
            )
          }}
        />
      )}
    </div>
  )
}
